import * as React from "react";

import { Box, Collapse } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";
import { FiCheck, FiHome, FiPackage, FiTruck } from "react-icons/fi";
import { IoIosArrowDown, IoIosArrowUp, IoMdClose } from "react-icons/io";
import { PiHourglass } from "react-icons/pi";
import { RiShipLine } from "react-icons/ri";
import { SiLighthouse } from "react-icons/si";

import { EventStatus } from "../../../models/marine.traffic";
import { IconTypeEnum, MarineEventsStatus } from "../../../util/enums";
import { brandSuccessColor } from "../../DashMetrics/DashStyleGuides";
import { ProgressBarContainerEvent } from "../index";
import { SectionTitle } from "../styles";
import { StepperContentItem } from "./components/ContentItem";
import { ContentItemText } from "./components/ContentItem/styles";
import EventStatusBadge from "./components/EventStatusBadge";
import useStyles, {
  ArrowContainer,
  CustomStepper,
  EmptySectionWrapper,
  MessageText,
  MessageTitle,
  StepContentContainer,
  StepIconContainer,
  StepItemBody,
  StepItemContainer,
  StepItemLine,
  StepLabelHeader,
  StepLocation,
  StepTitle,
  WrapperMessage,
} from "./styles";

const defaultIcon = require("Assets/img/no-gps.svg");

interface StatusProgressBarProps {
  steps: ProgressBarContainerEvent[];
  activeStep: number;
  isAirTracking?: boolean;
}
interface ColorlibStepIconProps {
  color: string;
  completed: boolean;
  pending: boolean;
  icon?: string;
  cancelled?: boolean;
  isAirTracking?: boolean;
}
function ColorlibStepIcon({
  color,
  completed,
  icon,
  pending,
  cancelled,
  isAirTracking,
}: ColorlibStepIconProps) {
  const iconProps = {
    size: 18,
    color: "#fff",
  };

  const renderSeaIcon = (icon: React.ReactNode) => {
    if (completed) return <FiCheck {...iconProps} />;
    switch (icon) {
      case IconTypeEnum.HOME:
        return cancelled ? <IoMdClose {...iconProps} /> : <FiHome {...iconProps} />;
      case IconTypeEnum.PACKAGE:
        return <FiPackage {...iconProps} />;
      case IconTypeEnum.TRUCK:
        return <FiTruck {...iconProps} />;
      case IconTypeEnum.PORT:
        return <SiLighthouse {...iconProps} />;
      case IconTypeEnum.WAITING:
        return <PiHourglass {...iconProps} color={colors?.gray500} />;

      default:
        return <RiShipLine {...iconProps} />;
    }
  };
  const renderAirIcon = (icon: React.ReactNode) => {
    if (completed) return <FiCheck {...iconProps} />;
    switch (icon) {
      case IconTypeEnum.HOME:
        return cancelled ? <IoMdClose {...iconProps} /> : <FiHome {...iconProps} />;
      default:
        return cancelled ? (
          <FiCheck {...iconProps} />
        ) : (
          <PiHourglass {...iconProps} color={colors?.gray500} />
        );
    }
  };

  const renderIcon = (icon: React.ReactNode) => {
    if (isAirTracking) return renderAirIcon(icon);

    return renderSeaIcon(icon);
  };

  return (
    <StepIconContainer color={pending ? colors?.gray200 : color}>
      {renderIcon(icon)}
    </StepIconContainer>
  );
}

export default function ShipmentStatus({
  activeStep,
  steps,
  isAirTracking,
}: StatusProgressBarProps) {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const [stepsOpened, setStepsOpened] = React.useState(isAirTracking ? [] : [0]);

  const toggleElement = (arr: number[], val: number) => {
    return arr.includes(val) ? arr.filter((el) => el !== val) : [...arr, val];
  };

  const handleToggleStep = (index: number) => {
    setStepsOpened((steps) => toggleElement(steps, index));
  };

  const renderArrow = (isOpen: boolean, hasEvents: boolean) => {
    if (!hasEvents) return null;
    const renderIcon = () => {
      const arrowProps = {
        color: colors.gray500,
        size: 16,
      };

      if (isOpen) return <IoIosArrowUp {...arrowProps} />;

      return <IoIosArrowDown {...arrowProps} />;
    };

    return <ArrowContainer className="arrow-container">{renderIcon()}</ArrowContainer>;
  };

  if (!steps?.length) {
    return (
      <div>
        <div>
          <SectionTitle>{translate("route")}</SectionTitle>
        </div>
        <EmptySectionWrapper>
          <img src={defaultIcon} />
          <WrapperMessage>
            <MessageTitle>{translate("routeUnavailable")}</MessageTitle>
            <MessageText>{translate("trackingInformationUnavailable")}</MessageText>
          </WrapperMessage>
        </EmptySectionWrapper>
      </div>
    );
  }

  return (
    <Box className={classes.root}>
      <div className={classes.titleMargin}>
        <SectionTitle>{translate("route")}</SectionTitle>
      </div>

      <CustomStepper>
        {steps.map((step, index) => {
          const isPending = activeStep < index;
          const isCurrent = activeStep === index;
          const isCompleted = activeStep > index;
          const hasEvents = !!step.events?.length;
          const isLastStep = !!(steps?.length === index + 1);
          const isOpen = stepsOpened.includes(index);
          const isCancelled = step?.status === MarineEventsStatus.CANCELLED;
          const isFirstStep = index === 0;

          const getSeaStepColor = () => {
            if (isCurrent) return brandSuccessColor;
            if (isPending) return colors.gray500;

            return colors?.success600;
          };

          const getAirStepColor = () => {
            if (isCancelled) return colors.error600;
            if (isPending || isCurrent) return colors.gray500;

            return colors.brandBlue600;
          };

          const getStepColor = () => {
            return isAirTracking ? getAirStepColor() : getSeaStepColor();
          };

          const getStepLineColor = () => {
            if (isCancelled) return colors.error600;

            if (activeStep - 1 === index) {
              return isAirTracking ? colors.brandBlue600 : brandSuccessColor;
            }
            if (activeStep - 1 < index) {
              return colors.gray200;
            }

            return isAirTracking ? colors.brandBlue600 : colors?.success600;
          };

          const getEventsColor = (status: string) => {
            if (EventStatus.DONE === status) {
              return colors.brandBlue600;
            }
            if (EventStatus.CANCELLED === status) {
              return colors.error600;
            }

            return colors.gray500;
          };

          const activeAccordion = () => {
            if (isAirTracking) {
              return hasEvents && !isFirstStep && !isLastStep;
            }
            return hasEvents;
          };

          return (
            <StepItemContainer key={index}>
              <div>
                <ColorlibStepIcon
                  pending={isPending || !!(isAirTracking && isCurrent)}
                  completed={isCompleted}
                  icon={isPending || (isAirTracking && isCurrent) ? "waiting" : step.iconName}
                  color={getStepColor()}
                  cancelled={isCancelled}
                  isAirTracking={isAirTracking}
                />
              </div>
              {!isLastStep ? (
                <StepItemLine orientation="vertical" flexItem currentColor={getStepLineColor()} />
              ) : null}
              <StepItemBody>
                <StepLabelHeader
                  className="step-label-header"
                  onClick={() => {
                    if (activeAccordion()) handleToggleStep(index);
                  }}
                  hasEvents={activeAccordion()}
                >
                  <StepTitle>{translate(step.eventTypeDescription)}</StepTitle>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignContent={"center"}
                    sx={{ width: "100%" }}
                  >
                    <StepLocation color={getStepColor()} wrap={isAirTracking}>
                      {step.eventLocationName}
                    </StepLocation>
                    {isCancelled && <EventStatusBadge value={step.status}></EventStatusBadge>}
                  </Box>
                  <ContentItemText>
                    {step?.nextEventDateTime ? step.nextEventDateTime : "\u00A0"}
                  </ContentItemText>
                  {isAirTracking && step?.vesselName && (isFirstStep || isLastStep) && (
                    <ContentItemText>{step.vesselName}</ContentItemText>
                  )}
                  {renderArrow(isOpen, activeAccordion())}
                </StepLabelHeader>
                <Collapse in={isOpen}>
                  <StepContentContainer>
                    {step.events?.map((event) => (
                      <StepperContentItem
                        color={isAirTracking ? getEventsColor(event.eventStatus) : getStepColor()}
                        key={event.eventTypeDescription}
                        event={event}
                        isAirTracking={isAirTracking}
                      />
                    ))}
                  </StepContentContainer>
                </Collapse>
              </StepItemBody>
            </StepItemContainer>
          );
        })}
      </CustomStepper>
    </Box>
  );
}
